// ConfirmDialog：点击确认二次确认对话框。
//
// 用于"破坏性 / 不可逆 / 批量"操作的前端二次拦截。当前 SPA 范围内的写操作大
// 多是单行可恢复（force-reload / metadata 编辑 / 单条命令），daemon 后端只对
// 批量 >20 行的 cache_apply / module_batch 强制 server-side typed-confirm
// nonce。前端 ConfirmDialog 即使在不强制 nonce 的场景也跑一遍，作为统一的
// UX 拦截层：用户看到 6 个字段的摘要后点击确认提交。
//
// 字段约定（plan.md "typed confirm 6 字段"）：
//   resource：被操作的对象类型（如 instance / machine_cache / module_batch）
//   action：动作（如 force_reload / batch_apply / module_disable）
//   target：被影响对象的标识（id / hostname / cache_root / 等）
//   actor：当前会话用户（外层注入）
//   ts：dialog 打开时间，仅展示
//   sig：可选签名（当前仅展示空字符串占位，server-side nonce 未来扩展）
//
// 用法：
//   <ConfirmDialog
//     open={open} onOpenChange={setOpen}
//     resource="instance" action="force_reload" target={id} actor={actor}
//     description="将触发业务 lua 热重载，不会重启 cq2"
//     onConfirm={async () => { await mutation.mutateAsync(); }}
//   />
//
// 实现要点：
//   - 在 dialog 打开时记录 ts；保持稳定（不随 re-render 变化）。
//   - 前端不再要求手工键入 target；需要服务端 nonce 的调用方仍自动携带
//     confirm_text=target，由后端校验 nonce 与 target 绑定关系。
//   - onConfirm 失败时 dialog 不自动关闭，让 toast/错误提示有机会显示。

import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource: string;
  action: string;
  target: string;
  actor: string;
  // 可选：附加 description 解释影响范围。
  description?: string;
  // 可选：sig 占位（如未来 server 下发 nonce 由上层传入）。
  sig?: string;
  // 可选：确认按钮 variant。
  confirmVariant?: "default" | "destructive";
  // 可选：操作需要额外字段（如 server_id）时，把对应表单 children 放进
  // dialog 内部固定位置，避免外挂浮层与对话框错位。
  extra?: React.ReactNode;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  resource,
  action,
  target,
  actor,
  description,
  sig,
  confirmVariant = "destructive",
  extra,
  onConfirm,
}: ConfirmDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const tsRef = useRef<number>(0);

  // dialog 打开时锁定 ts；关闭时重置 submitting。
  useEffect(() => {
    if (open) {
      tsRef.current = Date.now();
    } else {
      setSubmitting(false);
    }
  }, [open]);

  async function handleConfirm() {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // 错误由 onConfirm 内部 toast；dialog 保持打开。
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>确认 {action}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>

        {extra ? <div className="min-w-0 space-y-2">{extra}</div> : null}

        <dl className="grid min-w-0 grid-cols-[80px_minmax(0,1fr)] gap-y-1 text-xs">
          <dt className="text-[hsl(var(--muted-foreground))]">resource</dt>
          <dd className="break-all font-mono">{resource}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">action</dt>
          <dd className="break-all font-mono">{action}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">target</dt>
          <dd className="break-all font-mono">{target}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">actor</dt>
          <dd className="break-all font-mono">{actor}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">ts</dt>
          <dd className="break-all font-mono">
            {tsRef.current ? new Date(tsRef.current).toISOString() : ""}
          </dd>
          <dt className="text-[hsl(var(--muted-foreground))]">sig</dt>
          <dd className="break-all font-mono">{sig ?? ""}</dd>
        </dl>

        <DialogFooter>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            取消
          </Button>
          <Button
            type="button"
            size="sm"
            variant={confirmVariant}
            disabled={submitting}
            onClick={handleConfirm}
          >
            {submitting ? "提交中…" : "确认"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
